import { useCallback } from "react";
import {
  usePaginatedResource,
  PaginatedFetcher,
  pageFrom,
} from "./usePaginatedResource";
import { useEntity } from "./useEntity";
import { useEntityOperations } from "./useEntityOperations";
import { CommunityService } from "../services/communityService";
import {
  Post,
  CreatePostInput,
  UpdatePostInput,
  PostType,
} from "../types/community";

interface PostListFilters {
  filterType?: PostType | null;
  challengeId?: number | null;
}

export const usePosts = (
  initialPage: number = 1,
  initialFilter: PostType | null = null,
  initialChallengeId: number | null = null,
  initialLimit: number = 30
) => {
  const fetcher = useCallback<PaginatedFetcher<Post, PostListFilters>>(
    async (page, { filterType, challengeId }) => {
      const response = filterType
        ? await CommunityService.searchPostsByType(
            filterType,
            page,
            challengeId ?? null
          )
        : await CommunityService.getPosts(page);

      if (!response) return null;

      return pageFrom(response.data, {
        total: response.total,
        page: response.current_page,
        limit: response.per_page,
      });
    },
    []
  );

  const { items, filters, updateFilters, ...rest } = usePaginatedResource<
    Post,
    PostListFilters
  >(fetcher, {
    initialPage,
    initialLimit,
    initialFilters: {
      filterType: initialFilter,
      challengeId: initialChallengeId,
    },
    errorMessage: "Failed to fetch posts",
  });

  const setTypeFilter = useCallback(
    (type: PostType | null) => {
      updateFilters({
        filterType: type,
        // A challenge filter only means anything for challenge posts.
        challengeId: type === "challenge" ? filters.challengeId : null,
      });
    },
    [updateFilters, filters.challengeId]
  );

  const setChallengeFilter = useCallback(
    (id: number | null) => {
      updateFilters({ filterType: filters.filterType, challengeId: id });
    },
    [updateFilters, filters.filterType]
  );

  const clearFilters = useCallback(() => {
    updateFilters({ filterType: null, challengeId: null });
  }, [updateFilters]);

  return {
    posts: items,
    filterType: filters.filterType ?? null,
    challengeId: filters.challengeId ?? null,
    setTypeFilter,
    setChallengeFilter,
    clearFilters,
    ...rest,
  };
};

export const usePostOperations = () => {
  const { run, runBoolean, setError, ...rest } = useEntityOperations();

  const createPost = useCallback(
    (postData: CreatePostInput) =>
      run(() => CommunityService.createPost(postData), "Failed to create post"),
    [run]
  );

  const updatePost = useCallback(
    (postId: number, updates: UpdatePostInput) =>
      run(
        () => CommunityService.updatePost(postId, updates),
        "Failed to update post"
      ),
    [run]
  );

  const deletePost = useCallback(
    (postId: number) =>
      runBoolean(
        () => CommunityService.deletePost(postId),
        "Failed to delete post"
      ),
    [runBoolean]
  );

  // Like/unlike and pin/unpin stay hand-rolled: they return their own shape
  // to the optimistic UI and deliberately skip the shared loading flag, so a
  // like never greys out the surrounding list.
  const likePost = useCallback(
    async (postId: number) => {
      setError(null);
      try {
        const result = await CommunityService.likePost(postId);
        if (!result.success) {
          setError(result.error || "Failed to like post");
        }
        return {
          success: result.success,
          error: result.error,
        };
      } catch (err) {
        setError("An unexpected error occurred while liking post");
        return {
          success: false,
          error: "An unexpected error occurred while liking post",
        };
      }
    },
    [setError]
  );

  const unlikePost = useCallback(
    async (postId: number) => {
      setError(null);
      try {
        const result = await CommunityService.unlikePost(postId);
        if (!result.success) {
          setError(result.error || "Failed to unlike post");
        }
        return {
          success: result.success,
          error: result.error,
        };
      } catch (err) {
        setError("An unexpected error occurred while unliking post");
        return {
          success: false,
          error: "An unexpected error occurred while unliking post",
        };
      }
    },
    [setError]
  );

  const pinPost = useCallback(async (postId: number) => {
    try {
      const result = await CommunityService.pinPost(postId);
      return result.success;
    } catch (err) {
      return false;
    }
  }, []);

  const unpinPost = useCallback(async (postId: number) => {
    try {
      const result = await CommunityService.unpinPost(postId);
      return result.success;
    } catch (err) {
      return false;
    }
  }, []);

  return {
    createPost,
    updatePost,
    deletePost,
    likePost,
    unlikePost,
    pinPost,
    unpinPost,
    setError,
    ...rest,
  };
};

export const usePost = (postId: number | null) => {
  const fetcher = useCallback(
    (id: number) => CommunityService.getPostById(id),
    []
  );

  const { data, setData, ...rest } = useEntity<Post>(postId, fetcher, {
    notFoundMessage: "Post not found",
    errorMessage: "An unexpected error occurred while fetching post",
  });

  return { post: data, updatePostData: setData, ...rest };
};
